/**
 * LabCasesCard — Patient-scoped lab cases with expandable accordion rows.
 *
 * Composition mirrors PrescriptionsCard: clean table, single-row expand
 * revealing details/attachments/timeline inline. Zero navigation.
 *
 * Backend contract:
 *   GET  /lab-cases?patient_id=...
 *   POST /lab-cases
 *   PATCH /lab-cases/{id}/status
 *   DELETE /lab-cases/{id}                 (soft-cancel)
 *   GET  /laboratories?active_only=true
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { DateDMYInput } from "@/components/DateDMYInput";
import {
  FlaskConical, Plus, ChevronDown, ChevronRight, Eye, Pencil,
  Printer, X, Calendar, Clock, User2, Loader2, FileText,
} from "lucide-react";

const STATUS = {
  sent:         { label: "Sent",         color: "#8A3F1E", bg: "#FDF3ED" },
  lab_received: { label: "Received",     color: "#1F4B66", bg: "#E4EFF5" },
  in_progress:  { label: "In progress",  color: "#7A5A20", bg: "#F5EFE0" },
  ready:        { label: "Ready",        color: "#264029", bg: "#E0EBDE" },
  delivered:    { label: "Delivered",    color: "#2D6A4F", bg: "#E8F0EB" },
  fitted:       { label: "Fitted",       color: "#2D6A4F", bg: "#E8F0EB" },
  cancelled:    { label: "Cancelled",    color: "#6B7280", bg: "#EDEDED" },
};
const STATUS_ORDER = ["sent", "lab_received", "in_progress", "ready", "delivered", "fitted", "cancelled"];

const SHADES = ["A1","A2","A3","A3.5","A4","B1","B2","B3","B4","C1","C2","C3","C4","D2","D3","D4"];
const MATERIALS = ["Zirconia","PFM","Lithium Disilicate","E-max","Acrylic","Metal","Composite","Temporary Crown","PMMA","Cast Partial","Flexible Denture"];

// Exported so other surfaces (Data Migration workspace, bulk-import UIs) can
// render an identical Material picker without duplicating this list. Keep
// this the single source of truth for the app's material catalogue.
export { MATERIALS as LAB_MATERIALS, SHADES as LAB_SHADES };
const TREATMENT_TYPES = ["Crown","Bridge","Veneer","Denture","Partial Denture","Implant Prosthesis","CAD/CAM","Inlay/Onlay","Aligner","Night Guard","Retainer"];

export default function LabCasesCard({ patientId, patient }) {
  const [cases, setCases] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.get(`/lab-cases?patient_id=${patientId}`),
        api.get(`/laboratories?active_only=true`),
      ]);
      setCases(c.data || []);
      setLabs(l.data || []);
    } catch (e) {
      toast.error(apiError(e, "Failed to load lab cases"));
    } finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="card-widget" data-testid="lab-cases-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-pine" />
          <div className="overline text-ink-soft">Lab Cases</div>
        </div>
        <button
          type="button"
          data-testid="btn-create-lab-case"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-full bg-pine text-white text-sm font-medium px-4 py-2 hover:bg-pine/90"
        >
          <Plus className="w-4 h-4" /> Create Lab Case
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-ink-soft"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      ) : cases.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E7E1D3] p-10 text-center">
          <FlaskConical className="w-8 h-8 text-ink-soft mx-auto mb-3" />
          <div className="text-sm text-ink-mid">No lab cases yet for this patient.</div>
          <div className="text-xs text-ink-soft mt-1">
            Use "Create Lab Case" above, or tick "Send to Laboratory" on any Crown / Bridge / Veneer treatment.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lab-cases-table">
            <thead>
              <tr className="text-left text-ink-soft overline border-b border-[#EFEADC]">
                <th className="pb-3 pr-3 w-8"></th>
                <th className="pb-3 pr-4">Date sent</th>
                <th className="pb-3 pr-4">Tooth</th>
                <th className="pb-3 pr-4">Treatment</th>
                <th className="pb-3 pr-4">Laboratory</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Expected</th>
                <th className="pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <RowWithAccordion
                  key={c.id}
                  c={c}
                  labs={labs}
                  expanded={expanded === c.id}
                  onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
                  onEdit={() => { setEditing(c); }}
                  onChanged={load}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <LabCaseDialog
          existing={editing}
          patient={patient}
          labs={labs}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

// ─── Table row + inline accordion ────────────────────────────────────
function RowWithAccordion({ c, labs, expanded, onToggle, onEdit, onChanged }) {
  const meta = STATUS[c.status] || STATUS.sent;
  const cancel = async () => {
    if (!window.confirm("Cancel this lab case? This cannot be undone.")) return;
    try {
      await api.delete(`/lab-cases/${c.id}`);
      toast.success("Case cancelled");
      onChanged();
    } catch (e) { toast.error(apiError(e, "Cancel failed")); }
  };
  const printSlip = () => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    const attList = (c.attachments || []).map(a => `<li>${a.filename} <em>(${a.kind})</em></li>`).join("");
    w.document.write(`<html><head><title>Lab Slip · ${c.id.slice(0,8)}</title>
      <style>body{font-family:system-ui;padding:32px;color:#1a1a1a;max-width:800px;margin:0 auto;}
        h1{font-size:22px;margin:0 0 4px;} h2{font-size:13px;color:#666;text-transform:uppercase;letter-spacing:2px;margin:24px 0 8px;font-weight:500;}
        table{width:100%;border-collapse:collapse;margin-top:8px;} td{padding:6px 12px 6px 0;vertical-align:top;font-size:13px;}
        td:first-child{color:#666;width:180px;text-transform:uppercase;letter-spacing:1px;font-size:11px;}
        .bar{border-top:2px solid #1a3a2a;margin:16px 0;}
        ul{padding-left:20px;font-size:13px;}
      </style></head><body>
      <h1>Laboratory Work Order</h1>
      <div style="font-size:12px;color:#666">Case ID: ${c.id}</div>
      <div class="bar"></div>
      <h2>Case Details</h2>
      <table>
        <tr><td>Laboratory</td><td>${c.lab_name || "—"}</td></tr>
        <tr><td>Treatment</td><td>${c.treatment_type || "—"}</td></tr>
        <tr><td>Tooth Number(s)</td><td>${(c.tooth_numbers||[]).join(", ") || "—"}</td></tr>
        <tr><td>Shade</td><td>${c.shade || "—"}</td></tr>
        <tr><td>Material</td><td>${c.material || "—"}</td></tr>
        <tr><td>Priority</td><td>${(c.priority||"normal").toUpperCase()}</td></tr>
        <tr><td>Date Sent</td><td>${c.date_sent || "—"}</td></tr>
        <tr><td>Expected Delivery</td><td>${c.expected_delivery || "—"}</td></tr>
        <tr><td>Doctor</td><td>${c.doctor_name || "—"}</td></tr>
      </table>
      ${c.notes ? `<h2>Notes</h2><div style="font-size:13px;white-space:pre-wrap">${c.notes}</div>` : ""}
      ${attList ? `<h2>Attachments</h2><ul>${attList}</ul>` : ""}
      <div class="bar" style="margin-top:48px"></div>
      <div style="font-size:11px;color:#888">Signature: __________________________</div>
      </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 300);
  };

  return (
    <>
      <tr
        className="border-b border-[#F1F2F0] hover:bg-linen/40 cursor-pointer transition-colors"
        onClick={onToggle}
        data-testid={`lab-case-row-${c.id}`}
      >
        <td className="py-3 pr-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-ink-mid" /> : <ChevronRight className="w-4 h-4 text-ink-mid" />}
        </td>
        <td className="py-3 pr-4 text-ink-mid whitespace-nowrap">{c.date_sent || "—"}</td>
        <td className="py-3 pr-4 font-mono tabular-nums">{(c.tooth_numbers||[]).join(", ") || "—"}</td>
        <td className="py-3 pr-4 font-medium">{c.treatment_type}</td>
        <td className="py-3 pr-4">{c.lab_name || "—"}</td>
        <td className="py-3 pr-4">
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-medium"
                style={{ color: meta.color, background: meta.bg }}>
            {meta.label}
          </span>
        </td>
        <td className="py-3 pr-4 text-ink-mid whitespace-nowrap">{c.expected_delivery || "—"}</td>
        <td className="py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <IconAction icon={Eye}     onClick={onToggle}  label="View" testid={`view-${c.id}`}/>
          <IconAction icon={Pencil}  onClick={onEdit}    label="Edit" testid={`edit-${c.id}`}/>
          <IconAction icon={Printer} onClick={printSlip} label="Print slip" testid={`print-${c.id}`}/>
          {c.status !== "cancelled" && (
            <IconAction icon={X} onClick={cancel} label="Cancel" testid={`cancel-${c.id}`} danger />
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-[#F1F2F0] bg-[#FBFAF6]">
          <td colSpan={8} className="p-6" data-testid={`lab-case-details-${c.id}`}>
            <AccordionDetails c={c} labs={labs} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function IconAction({ icon: Icon, onClick, label, testid, danger }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} title={label}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ml-1 ${
              danger ? "text-[#B94A48] hover:bg-[#FCECEC]" : "text-ink-mid hover:bg-linen hover:text-ink"}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

// ─── Accordion body — details, attachments, timeline, status changer ──
function AccordionDetails({ c, labs, onChanged }) {
  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/lab-cases/${c.id}/status`, { status: newStatus });
      toast.success(`Status → ${STATUS[newStatus].label}`);
      onChanged();
    } catch (e) { toast.error(apiError(e, "Status update failed")); }
  };

  const attachments = c.attachments || [];
  const photos = attachments.filter(a => a.kind?.startsWith("photo_"));
  const scans  = attachments.filter(a => ["stl","ply","obj","impression"].includes(a.kind));
  const rx     = attachments.filter(a => a.kind === "prescription");
  const docs   = attachments.filter(a => a.kind === "document");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Case details */}
      <div>
        <div className="overline text-ink-soft mb-2">Case details</div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Def label="Tooth" val={(c.tooth_numbers||[]).join(", ")||"—"} />
          <Def label="Treatment" val={c.treatment_type} />
          <Def label="Shade" val={c.shade || "—"} />
          <Def label="Material" val={c.material || "—"} />
          <Def label="Laboratory" val={c.lab_name || "—"} />
          <Def label="Priority" val={(c.priority||"normal").toUpperCase()} />
          <Def label="Date sent" val={c.date_sent || "—"} />
          <Def label="Expected" val={c.expected_delivery || "—"} />
          <Def label="Doctor" val={c.doctor_name || "—"} />
        </dl>
        {c.notes && (
          <div className="mt-4 rounded-xl bg-white border border-[#EFEADC] p-3 text-xs text-ink/80 whitespace-pre-wrap">{c.notes}</div>
        )}

        {/* Status changer */}
        <div className="mt-5">
          <div className="overline text-ink-soft mb-2">Change status</div>
          <div className="flex flex-wrap gap-1.5" data-testid={`status-changer-${c.id}`}>
            {STATUS_ORDER.filter(s => s !== "cancelled").map(s => {
              const active = c.status === s;
              return (
                <button key={s} type="button" onClick={() => !active && changeStatus(s)}
                        disabled={active}
                        data-testid={`set-status-${s}-${c.id}`}
                        className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full font-medium transition ${
                          active
                            ? "bg-pine text-white cursor-default"
                            : "bg-white border border-[#E8EAE9] text-ink-mid hover:border-pine hover:text-ink"}`}>
                  {STATUS[s].label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <div className="overline text-ink-soft mb-2">Timeline</div>
        <div className="relative">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-[#E7E1D3]"></div>
          <div className="space-y-3">
            {(c.timeline||[]).map((t, i) => (
              <div key={i} className="relative pl-8">
                <div className="absolute left-0.5 top-1 w-3 h-3 rounded-full bg-pine border-2 border-white"></div>
                <div className="text-sm text-ink font-medium">{STATUS[t.kind]?.label || t.kind.replace("_"," ")}</div>
                <div className="text-[11px] text-ink-soft">
                  {new Date(t.at).toLocaleString("en-IN", {dateStyle:"medium", timeStyle:"short"})}
                  {t.by_name && ` · ${t.by_name}`}
                </div>
                {t.note && <div className="text-xs text-ink-mid mt-0.5">{t.note}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attachments */}
      <div className="lg:col-span-2 space-y-4">
        {rx.length > 0 && <AttGroup title="Prescription" items={rx} testid="rx-group" />}
        {scans.length > 0 && <AttGroup title="Impressions / IOS scans" items={scans} testid="scans-group" />}
        {photos.length > 0 && <PhotoGallery items={photos} />}
        {docs.length > 0 && <AttGroup title="Documents" items={docs} testid="docs-group" />}
        {attachments.length === 0 && (
          <div className="text-xs text-ink-soft italic">No attachments yet.</div>
        )}
      </div>
    </div>
  );
}

function Def({ label, val }) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-widest text-ink-soft self-center">{label}</dt>
      <dd className="text-sm text-ink">{val}</dd>
    </>
  );
}

function AttGroup({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-ink-soft mb-2">{title}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {items.map((a) => (
          <div key={a.id || a.url} className="rounded-xl bg-white border border-[#EFEADC] p-3 text-xs">
            <div className="flex items-center gap-2 mb-2 text-ink-mid">
              <FileText className="w-3.5 h-3.5" />
              <span className="truncate flex-1">{a.filename}</span>
            </div>
            <div className="flex gap-2">
              <a href={a.url} target="_blank" rel="noopener noreferrer"
                 className="flex-1 text-center text-[11px] rounded-full bg-linen text-ink px-2 py-1 hover:bg-[#F5F0E3]">View</a>
              <a href={a.url} download={a.filename}
                 className="flex-1 text-center text-[11px] rounded-full bg-pine text-white px-2 py-1 hover:bg-pine/90">Download</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhotoGallery({ items }) {
  const [lightbox, setLightbox] = useState(null);
  return (
    <div>
      <div className="overline text-ink-soft mb-2">Clinical photos</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {items.map((p) => (
          <button key={p.id || p.url} onClick={() => setLightbox(p)}
                  className="aspect-square rounded-xl overflow-hidden bg-linen border border-[#EFEADC] group relative">
            <img src={p.url} alt={p.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            <span className="absolute bottom-1 left-1 right-1 text-[8px] uppercase tracking-widest bg-black/60 text-white px-1.5 py-0.5 rounded truncate">
              {p.kind.replace("photo_","")}
            </span>
          </button>
        ))}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-pointer"
             onClick={() => setLightbox(null)}>
          <img src={lightbox.url} alt={lightbox.filename} className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}

// ─── Create / Edit dialog ────────────────────────────────────────────
function LabCaseDialog({ existing, patient, labs, onClose, onSaved }) {
  const [form, setForm] = useState(existing || {
    patient_id: patient.id,
    treatment_type: TREATMENT_TYPES[0],
    tooth_numbers: [],
    shade: "",
    material: "",
    lab_id: labs[0]?.id || "",
    date_sent: new Date().toISOString().slice(0,10),
    expected_delivery: "",
    priority: "normal",
    status: "sent",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [toothInput, setToothInput] = useState((form.tooth_numbers || []).join(", "));

  const save = async () => {
    if (!form.lab_id) { toast.error("Select a laboratory"); return; }
    if (!form.treatment_type) { toast.error("Select treatment"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tooth_numbers: toothInput.split(",").map(t => t.trim()).filter(Boolean),
      };
      if (existing) {
        await api.patch(`/lab-cases/${existing.id}`, payload);
        toast.success("Lab case updated");
      } else {
        await api.post(`/lab-cases`, payload);
        toast.success("Lab case created");
      }
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
         onClick={onClose} data-testid="lab-case-dialog">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-[#E7E1D3] max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#EFEADC] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="overline text-ink-soft">Lab case</div>
            <div className="font-display text-2xl mt-1">{existing ? "Edit case" : "Create Lab Case"}</div>
          </div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink" data-testid="dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormField label="Treatment">
            <select value={form.treatment_type} data-testid="fld-treatment"
                    onChange={(e) => setForm({...form, treatment_type: e.target.value})}
                    className="input w-full">
              {TREATMENT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </FormField>
          <FormField label="Tooth number(s) — comma separated (e.g. 16, 26)">
            <input value={toothInput} onChange={(e) => setToothInput(e.target.value)}
                   data-testid="fld-teeth"
                   placeholder="16, 26"
                   className="input w-full" />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Shade">
              <select value={form.shade || ""} data-testid="fld-shade"
                      onChange={(e) => setForm({...form, shade: e.target.value})}
                      className="input w-full">
                <option value="">—</option>
                {SHADES.map(s => <option key={s}>{s}</option>)}
              </select>
            </FormField>
            <FormField label="Material">
              <select value={form.material || ""} data-testid="fld-material"
                      onChange={(e) => setForm({...form, material: e.target.value})}
                      className="input w-full">
                <option value="">—</option>
                {MATERIALS.map(m => <option key={m}>{m}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Laboratory">
            {labs.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#EAC5AE] bg-[#FDF3ED] p-3 text-xs text-[#8A3F1E]">
                No active labs configured. Add one from <b>Settings → Laboratories</b> first.
              </div>
            ) : (
              <select value={form.lab_id} data-testid="fld-lab"
                      onChange={(e) => setForm({...form, lab_id: e.target.value})}
                      className="input w-full">
                {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Date sent">
              <DateDMYInput value={form.date_sent || ""} data-testid="fld-date-sent"
                     onChange={(e) => setForm({...form, date_sent: e.target.value})}
                     className="input w-full" />
            </FormField>
            <FormField label="Expected delivery">
              <DateDMYInput value={form.expected_delivery || ""} data-testid="fld-eta"
                     onChange={(e) => setForm({...form, expected_delivery: e.target.value})}
                     className="input w-full" />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Priority">
              <select value={form.priority} data-testid="fld-priority"
                      onChange={(e) => setForm({...form, priority: e.target.value})}
                      className="input w-full">
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </FormField>
            <FormField label="Status">
              <select value={form.status} data-testid="fld-status"
                      onChange={(e) => setForm({...form, status: e.target.value})}
                      className="input w-full">
                {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS[s].label}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Notes">
            <textarea value={form.notes || ""} rows={3} data-testid="fld-notes"
                      onChange={(e) => setForm({...form, notes: e.target.value})}
                      placeholder="Any instructions for the lab technician…"
                      className="input w-full resize-none" />
          </FormField>
        </div>
        <div className="p-6 border-t border-[#EFEADC] flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="btn-cancel"
                  className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen">Cancel</button>
          <button onClick={save} disabled={saving || labs.length === 0}
                  data-testid="btn-save-case"
                  className="text-sm rounded-full bg-pine text-white px-6 py-2 font-medium hover:bg-pine/90 disabled:opacity-40">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />}
            {existing ? "Save changes" : "Create case"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <div className="text-xs text-ink-mid mb-1">{label}</div>
      {children}
    </div>
  );
}
